import { useEffect, useState } from "react";
import Button from "@/components/Button.jsx";
import { NAV_LINKS, SITE } from "@/config";

/* Inline CSS: deterministic underline and visibility */
const styles = `
  header.navbar { position: fixed; inset-inline: 0; top: 0; z-index: 50; }
  header.navbar nav { height: 64px; display: flex; align-items: center; justify-content: space-between; overflow: visible; }

  .navlink { 
    position: relative; 
    display: inline-flex; 
    flex-direction: column; 
    align-items: center; 
    justify-content: center; 
    height: 32px; 
    line-height: 1; 
    padding-bottom: 2px; 
    overflow: hidden;
  }

  .navtext { 
    transition: color .18s ease; 
  }

  /* Underline bar (default hidden) */
  .underline-bar { 
    margin-top: 6px; 
    height: 2px; 
    width: 0; 
    border-radius: 9999px; 
    background: var(--color-primary); 
    opacity: .0; 
    transition: width .25s ease, opacity .25s ease; 
    pointer-events: none; 
  }

  /* Active link = full underline */
  .navlink[data-active="true"] .navtext { color: var(--color-primary); }
  .navlink[data-active="true"] .underline-bar { width: 100%; opacity: 1; }

  /* Hover effect = underline grows halfway */
  .navlink:hover .underline-bar:not([data-active="true"]) {
    width: 70%;
    opacity: .8;
  }

  /* Optional shimmer animation when hovering */
  .navlink:hover .underline-bar {
    animation: shimmer 1.2s linear infinite;
  }

  @keyframes shimmer {
    0% { opacity: .6; filter: brightness(1); }
    50% { opacity: 1; filter: brightness(1.3); }
    100% { opacity: .6; filter: brightness(1); }
  }

  /* Header surface with contrast */
  header.navbar.scrolled { backdrop-filter: saturate(140%) blur(8px); }
  header.navbar.scrolled.light { background: color-mix(in oklab, var(--color-bg) 85%, transparent); border-bottom: 1px solid rgba(0,0,0,.06); box-shadow: 0 2px 12px rgba(0,0,0,.06); }
  header.navbar.scrolled.dark { background: color-mix(in oklab, var(--color-bg-dark) 85%, transparent); border-bottom: 1px solid rgba(255,255,255,.08); box-shadow: 0 2px 12px rgba(0,0,0,.35); }
`;

/* Smooth scroll with header offset */
function scrollToSection(id, offset = 64) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: "smooth" });
}

/* Robust active section without IntersectionObserver */
function useActiveSection(ids, { headerOffset = 64, tick = 100 } = {}) {
  const [active, setActive] = useState(ids[0] || "");
  useEffect(() => {
    const getActive = () => {
      const y = window.scrollY + headerOffset + 1;
      let bestId = ids[0] || "";
      let bestTop = Infinity;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = Math.abs(el.offsetTop - y);
        if (top < bestTop) {
          bestTop = top;
          bestId = id;
        }
      }
      return bestId;
    };

    const update = () => setActive((prev) => {
      const id = getActive();
      return prev === id ? prev : id;
    });

    let t;
    const onScroll = () => { clearTimeout(t); t = setTimeout(update, tick); };
    const onResize = () => { clearTimeout(t); t = setTimeout(update, 50); };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("hashchange", update);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("hashchange", update);
    };
  }, [ids.join("|"), headerOffset, tick]);

  return [active, setActive];
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sectionIds = NAV_LINKS.map((l) => l.href.replace("#", ""));
  const [active, setActive] = useActiveSection(sectionIds, { headerOffset: 64 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Detect theme class on <html> or <body> to pick header surface style
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      <header className={`navbar ${scrolled ? `scrolled ${isDark ? "dark" : "light"}` : ""}`}>
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("hero");
              setActive("hero");
              setOpen(false);
              history.replaceState(null, "", "#hero");
            }}
            className="text-xl font-display font-semibold tracking-tight"
          >
            {(SITE.name?.split(" ")[0] || "Ishan")}
            <span className="text-[--color-primary]">.</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) => {
              const id = link.href.replace("#", "");
              const isActive = active === id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  data-active={isActive ? "true" : "false"}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(id);
                    setActive(id);
                    history.replaceState(null, "", `#${id}`);
                  }}
                  className="navlink group"
                >
                  <span className="navtext text-sm text-gray-700 dark:text-gray-300 group-hover:text-[--color-primary]">
                    {link.label}
                  </span>
                  <span className="underline-bar" aria-hidden="true" />
                </a>
              );
            })}

            <Button
              as="a"
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("contact");
                setActive("contact");
                history.replaceState(null, "", "#contact");
              }}
              variant="glow"
              size="sm"
            >
              Let’s chat
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <button
              aria-label="Toggle menu"
              onClick={() => setOpen((s) => !s)}
              className="relative h-10 w-10 grid place-items-center rounded-md border border-white/10 hover:border-white/20"
            >
              <div className={["h-[1.5px] w-5 bg-current transition-all", open ? "rotate-45 translate-y-[1.5px]" : "-translate-y-1.5"].join(" ")} />
              <div className={["h-[1.5px] w-5 bg-current transition-opacity", open ? "opacity-0" : "opacity-100"].join(" ")} />
              <div className={["h-[1.5px] w-5 bg-current transition-all", open ? "-rotate-45 -translate-y-[1.5px]" : "translate-y-1.5"].join(" ")} />
            </button>
          </div>
        </nav>

        {open && (
          <div className="md:hidden border-t border-white/10 bg-[--color-bg]/90 dark:bg-[--color-bg-dark]/90 backdrop-blur-md">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-2">
              {NAV_LINKS.map((link) => {
                const id = link.href.replace("#", "");
                const isActive = active === id;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    data-active={isActive ? "true" : "false"}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(id);
                      setActive(id);
                      history.replaceState(null, "", `#${id}`);
                      setOpen(false);
                    }}
                    className="navlink group items-start"
                  >
                    <span className="navtext text-sm text-gray-700 dark:text-gray-300 group-hover:text-[--color-primary]">
                      {link.label}
                    </span>
                    <span className="underline-bar" aria-hidden="true" />
                  </a>
                );
              })}
              <Button
                as="a"
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("contact");
                  if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 64;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }}
                variant="glow"
                size="sm"
                textForceBlack // keep text black even in dark mode
              >
                Let’s chat
              </Button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}